import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { post } from "@rails/request.js"
import { showAlertModal } from "../lib/custom_confirm"
import { setButtonLoading } from "../lib/button_loading"

// The signature preview. POSTs the CURRENT editor HTML (unsaved edits
// included — read from the TipTap hidden input, which the editor syncs on
// every keystroke) to the preview endpoint, which sanitizes it and resolves
// {{agent_*}} tokens against the signed-in agent. Two modes:
//
// - live (Settings → Signature, #18893): the body target is an inline panel
//   beside the editor; it renders on connect and again a moment after each
//   edit (`input->signature-preview#refresh` on the host). The newest request
//   wins, and a failure says so in the panel instead of a popup per keystroke.
// - popup (the sign-up wizard's View Signature, #17129): `open` fills the
//   modal's body and shows it.
export const LIVE_REFRESH_DELAY_MS = 400
export const EMPTY_SIGNATURE_HTML = '<p class="text-muted mb-0">Your signature is empty.</p>'
export const LIVE_PREVIEW_FAILED_HTML = '<p class="text-muted mb-0">The preview could not be built. It will try again as you type.</p>'

export default class extends Controller {
  static targets = ["modal", "body", "trigger"]
  static values = { url: String, inputId: String, live: Boolean }

  connect() {
    this._sequence = 0
    if (this.liveValue) this.render()
  }

  disconnect() {
    clearTimeout(this._timer)
  }

  refresh() {
    if (!this.liveValue) return
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this.render(), LIVE_REFRESH_DELAY_MS)
  }

  async render() {
    const sequence = ++this._sequence
    let html = LIVE_PREVIEW_FAILED_HTML
    try {
      const response = await post(this.urlValue, {
        body: { signature: this.currentSignatureHtml() },
        responseKind: "json",
      })
      if (response.ok) html = this.previewHtml((await response.response.json()).html)
    } catch (_networkError) {
      // The panel says so below; the next edit tries again.
    }
    // A slower, older response never overwrites a newer one.
    if (sequence === this._sequence) this.bodyTarget.innerHTML = html
  }

  async open(event) {
    event.preventDefault()
    // One preview at a time — a slow response shouldn't queue up N modals.
    if (this._loading) return
    this._loading = true
    // BUTTON-8: shared spinner + aria-busy, label kept.
    if (this.hasTriggerTarget) setButtonLoading(this.triggerTarget, true)

    try {
      let response
      try {
        response = await post(this.urlValue, {
          body: { signature: this.currentSignatureHtml() },
          responseKind: "json",
        })
      } catch (_networkError) {
        await showAlertModal("Could not reach the server. Check your connection and try again.",
          { variant: "warning", title: "Preview failed" })
        return
      }

      if (!response.ok) {
        await showAlertModal("Could not build the preview. Please try again.",
          { variant: "warning", title: "Preview failed" })
        return
      }

      const { html } = await response.response.json()
      this.bodyTarget.innerHTML = this.previewHtml(html)
      Modal.getOrCreateInstance(this.modalTarget).show()
    } finally {
      this._loading = false
      if (this.hasTriggerTarget) setButtonLoading(this.triggerTarget, false)
    }
  }

  // Server-sanitized (signature allowlist) — safe to inject.
  previewHtml(html) {
    return html?.trim() ? html : EMPTY_SIGNATURE_HTML
  }

  currentSignatureHtml() {
    const input = document.getElementById(this.inputIdValue)
    return input ? input.value : ""
  }
}
